/**
 * BicepGuard — route-level guard + role provider for the BICEP module.
 *
 *  1. Requires a signed-in user (else → /login, remembering where they were going;
 *     the bare /bicep root goes to the public landing page instead).
 *  2. Checks once, via bicepGetInfo, that BICEP is enabled for this institute.
 *  3. Provides { enabled, isCoordinator, isBicepAdmin } to everything below via
 *     BicepContext, so the shell and pages don't each re-fetch it.
 *
 * Coordinator-only pages are wrapped with <RequireCoordinator> inside the same
 * layout route, so moving between user and admin pages never remounts the shell.
 */
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { BicepContext, useBicep, useBicepInfo } from "@/lib/bicep";
import { ErrorBlock } from "@/components/bicep/kit";

function Spinner() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }} role="status" aria-label="Loading BICEP">
      <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #e5e0d5", borderTopColor: "#1346E8", animation: "bicep-spin 0.8s linear infinite" }} />
      <style>{"@keyframes bicep-spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}

export default function BicepGuard({ children }) {
  const { isAuthed } = useAuth();
  const location = useLocation();
  const info = useBicepInfo();
  const unavailable = isAuthed && !info.loading && !info.error && !info.enabled;

  // Side effects belong in an effect, not in render.
  useEffect(() => {
    if (unavailable) toast.error("BICEP is not available for your institute.", { id: "bicep-unavailable" });
  }, [unavailable]);

  // A signed-out visitor opening the BICEP root sees the public landing page; deep links go to sign-in.
  if (!isAuthed) return location.pathname === "/bicep" ? <Navigate to="/about-bicep" replace /> : <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (info.loading) return <Spinner />;
  if (info.error) {
    return (
      <div className="max-w-md mx-auto px-4 py-20">
        <ErrorBlock error={info.error} onRetry={info.retry} />
      </div>
    );
  }
  if (unavailable) return <Navigate to="/portal" replace />;

  return <BicepContext.Provider value={info}>{children}</BicepContext.Provider>;
}

/**
 * Role gates, used per-route inside the single BICEP layout:
 *   <RequireAdmin>        whole-process pages (applications, incubatees, funding, comms, people…)
 *   <RequireCoordinator>  programme pages (events, competitions, schemes, mentors, resources, problems);
 *                         admins pass too
 *   <RequireMentor>       the mentor workspace
 */
function Gate({ allowed, message, children }) {
  useEffect(() => { if (!allowed) toast.error(message, { id: "bicep-role-gate" }); }, [allowed, message]);
  return allowed ? children : <Navigate to="/bicep" replace />;
}
export function RequireAdmin({ children }) {
  const { isBicepAdmin } = useBicep();
  return <Gate allowed={isBicepAdmin} message="BICEP admin access required.">{children}</Gate>;
}
export function RequireCoordinator({ children }) {
  const { isCoordinator } = useBicep();
  return <Gate allowed={isCoordinator} message="Coordinator access required.">{children}</Gate>;
}
export function RequireMentor({ children }) {
  const { isMentor } = useBicep();
  return <Gate allowed={isMentor} message="This page is for BICEP mentors.">{children}</Gate>;
}
